import { useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, KeyboardEvent, Platform } from 'react-native';
import WebView from 'react-native-webview';
import { Box } from 'native-base';

import { useLiveChat } from '../store/chat.store';

const SNAP_ENGAGE_SRC = `https://storage.googleapis.com/code.snapengage.com/js/d4891d37-1860-4689-8abb-ba23ed9f127b.js`;

export const LiveChat = () => {
  const { payload, isOpen, email, username } = useLiveChat();
  const webViewRef = useRef<WebView>(null);
  const isOfflineModeRef = useRef(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Native-only: HTML template for WebView
  const html = useMemo(() => {
    const payloadScript = payload
      ? Object.entries(payload)
          .filter(([, v]) => v != null)
          .map(([k, v]) => `window[${JSON.stringify(k)}] = ${JSON.stringify(String(v))};`)
          .join('\n          ')
      : '';

    return `<!DOCTYPE html>
<html>
  <head>
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <style>html, body { background: transparent !important; margin: 0; padding: 0; }</style>
  </head>
  <body style="background: transparent">
    <script>
      (function () {
        var offlineMode = false;
        ${payloadScript}

        function send(msg) {
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(msg);
          } else {
            window.parent.postMessage(msg, '*');
          }
        }

        function attachAnchorListener(doc) {
          doc.addEventListener('click', function (e) {
            var el = e.target;
            while (el && el.tagName !== 'A') el = el.parentElement;
            if (!el || (el.getAttribute('href') || '').trim() !== '#') return;
            e.preventDefault();
            if (!offlineMode) {
              var text = (el.textContent || '').toLowerCase().trim();
              if (text.includes('leave a message') || text.includes('send a message')) {
                offlineMode = true;
                send('offlineMode');
              }
            }
          }, true);
        }

        attachAnchorListener(document);

        function onNestedFrameLoad(frame) {
          try {
            var frameDoc = frame.contentDocument || frame.contentWindow.document;
            attachAnchorListener(frameDoc);
            var nameVal = window['name'];
            if (nameVal && frame.contentWindow) frame.contentWindow.name = nameVal;
            var storeVal = window['storeId'];
            if (!storeVal) return;
            var fill = function () {
              var inputs = frameDoc.querySelectorAll('input');
              for (var i = 0; i < inputs.length; i++) {
                if ((inputs[i].placeholder || '').toLowerCase().indexOf('store') !== -1 && !inputs[i].value) {
                  inputs[i].value = storeVal;
                  inputs[i].dispatchEvent(new Event('input', { bubbles: true }));
                  inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
                }
              }
            };
            fill();
            new MutationObserver(fill).observe(frameDoc.body || frameDoc.documentElement, { childList: true, subtree: true });
          } catch (e) {}
        }

        new MutationObserver(function (mutations) {
          mutations.forEach(function (mutation) {
            mutation.addedNodes.forEach(function (node) {
              if (node.nodeType !== 1) return;
              var frames = node.tagName === 'IFRAME'
                ? [node]
                : Array.prototype.slice.call(node.querySelectorAll('iframe'));
              frames.forEach(function (frame) {
                var nameVal = window['name'];
                if (nameVal) frame.setAttribute('name', nameVal);
                frame.addEventListener('load', function () { onNestedFrameLoad(frame); });
              });
            });
          });
        }).observe(document.documentElement, { childList: true, subtree: true });

        var se = document.createElement('script');
        se.async = true;
        se.src = '${SNAP_ENGAGE_SRC}';
        se.onload = function () {
          var _prefillEmail = ${JSON.stringify(email ?? '')};
          var _prefillName  = ${JSON.stringify(username ?? '')};
          var _prefillStore = ${JSON.stringify(String(payload?.storeId ?? ''))};
          if (_prefillName) window['name'] = _prefillName;

          function applyPrefill() {
            if (_prefillEmail) SnapEngage.setUserEmail(_prefillEmail, true);
            if (_prefillName)  SnapEngage.setUserName(_prefillName);
            if (_prefillStore) SnapEngage.setCustomField('StoreNumber', _prefillStore);
          }

          applyPrefill();

          SnapEngage.setCallback('Minimize', function (minimized) {
            if (!minimized) applyPrefill();
            send(minimized ? 'minimize' : 'restore');
          });

          SnapEngage.setCallback(SnapEngage.callbacks.CLOSE, function () {
            if (!offlineMode) send('close');
          });

          SnapEngage.setCallback(SnapEngage.callbacks.CHAT_ENDED, function () {
            if (offlineMode) {
              offlineMode = false;
              send('clearOfflineMode');
            }
          });

          SnapEngage.startLink();
        };

        document.body.appendChild(se);
      })();
    <\/script>
  </body>
</html>`;
  }, [payload, email, username]);

  // Web: inject SnapEngage directly into the parent document so that:
  // - document.URL is the real page URL  → fixes FROM URL in transcripts
  // - window globals are in the correct scope → fixes {js:name} substitution
  useEffect(() => {
    if (Platform.OS !== 'web' || !isOpen) return;

    const win = window as any;
    // SnapEngage injects its widget UI (chat window, minimize bubble, proactive popover, launcher button, etc.)
    // as sibling nodes directly under <body> — it exposes no public API to tear all of that down at once,
    // so snapshot what's already there and diff against it on cleanup to remove everything it added.
    const bodyChildrenBeforeInject = new Set(Array.from(document.body.children));

    if (payload) {
      Object.entries(payload)
        .filter(([, v]) => v != null)
        .forEach(([k, v]) => {
          win[k] = String(v);
        });
    }

    const applyPrefill = () => {
      const SE = win.SnapEngage;
      if (!SE) return;
      if (email) SE.setUserEmail(email, true);
      if (username) SE.setUserName(username);
      if (payload?.storeId) SE.setCustomField('StoreNumber', String(payload.storeId));
    };

    const init = () => {
      const SE = win.SnapEngage;
      if (!SE) return;
      applyPrefill();
      SE.setCallback('Minimize', (minimized: boolean) => {
        if (!minimized) applyPrefill();
        useLiveChat.getState()[minimized ? 'minimize' : 'restore']();
      });
      SE.setCallback(SE.callbacks.CLOSE, () => {
        SE.hideButton();
        useLiveChat.getState().reset();
      });
      SE.startLink();
    };

    if (win.SnapEngage) {
      init();
    } else {
      const se = document.createElement('script');
      se.src = SNAP_ENGAGE_SRC;
      se.async = true;
      se.onload = init;
      document.body.appendChild(se);
    }

    return () => {
      win.SnapEngage?.hideButton?.();
      win.SnapEngage?.clearAllCookies?.();
      // Remove every DOM node SnapEngage injected into <body> since this effect ran,
      // otherwise the widget UI stays visible even after isOpen flips false (e.g. on logout).
      Array.from(document.body.children).forEach(node => {
        if (!bodyChildrenBeforeInject.has(node)) node.remove();
      });
      // Remove the script so next open re-injects a fresh SnapEngage instance
      // (clears in-memory session state, matching the old iframe isolation behavior)
      document.querySelector(`script[src="${SNAP_ENGAGE_SRC}"]`)?.remove();
      delete win.SnapEngage;
    };
  }, [isOpen, payload, email, username]);

  // Native: synchronously clear SnapEngage cookies when the store resets,
  // before React re-renders and the WebView unmounts.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    let prevIsOpen = useLiveChat.getState().isOpen;
    return useLiveChat.subscribe(state => {
      if (prevIsOpen && !state.isOpen) {
        webViewRef.current?.injectJavaScript(
          'if(typeof SnapEngage!=="undefined"){SnapEngage.clearAllCookies();}true;',
        );
      }
      prevIsOpen = state.isOpen;
    });
  }, []);

  useEffect(() => {
    if (!isOpen || Platform.OS === 'web') {
      isOfflineModeRef.current = false;
      return;
    }

    const handler = (e: MessageEvent) => {
      if (typeof e.data !== 'string') return;
      if (e.data === 'offlineMode') { isOfflineModeRef.current = true; return; }
      if (e.data === 'clearOfflineMode') { isOfflineModeRef.current = false; return; }
      if (e.data === 'minimize') useLiveChat.getState().minimize();
      if (e.data === 'restore') useLiveChat.getState().restore();
      if (e.data === 'close' && !isOfflineModeRef.current) useLiveChat.getState().reset();
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isOpen]);

  // Native: keep the chat window above the keyboard when it's open.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const show = Keyboard.addListener('keyboardWillShow', (e: KeyboardEvent) => setKeyboardHeight(e.endCoordinates.height));
    const hide = Keyboard.addListener('keyboardWillHide', () => setKeyboardHeight(0));
    return () => {
      show.remove();
      hide.remove();
    };
  }, []);

  // Web: SnapEngage injects its own fixed-position UI into the parent document.
  if (Platform.OS === 'web') return null;

  if (!isOpen) return null;

  return (
    <Box
      position="absolute"
      zIndex={9999}
      right={0}
      width={380}
      borderWidth={0}
      overflow="hidden"
      shadow={0}
      style={{ bottom: keyboardHeight, height: 500 }}
    >
      <WebView
        ref={webViewRef}
        source={{ html, baseUrl: typeof payload?.pageUrl === 'string' ? payload.pageUrl : undefined }}
        style={{ flex: 1, backgroundColor: 'transparent' }}
        scrollEnabled={false}
        onMessage={event => {
          const { data } = event.nativeEvent;
          if (data === 'offlineMode') { isOfflineModeRef.current = true; return; }
          if (data === 'clearOfflineMode') { isOfflineModeRef.current = false; return; }
          if (data === 'minimize') useLiveChat.getState().minimize();
          if (data === 'restore') useLiveChat.getState().restore();
          if (data === 'close' && !isOfflineModeRef.current) useLiveChat.getState().reset();
        }}
      />
    </Box>
  );
};
